/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 08 2024
 **/

import { ForwardedRef, RefObject, useEffect, useState } from "react";

export default function useIntersectionObserverWithRef<E extends HTMLElement>(
	ref: RefObject<E> | ForwardedRef<E>,
	callback?: (node: IntersectionObserverEntry) => void,
	dependencies: React.DependencyList = [],
	options: IntersectionObserverInit = { threshold: 1, root: null, rootMargin: "0px 0px 0px 0px" }
) {
	const [isShow, setIsShow] = useState(false);

	useEffect(() => {
		const target = ref;
		if (!target || typeof target === "function") return;
		if (!target.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				setIsShow(entries[0].isIntersecting);
				callback && callback(entries[0]);
			},
			{ ...options }
		);
		observer.observe(target.current);
		return () => {
			if (!target.current) return;
			observer.unobserve(target.current);
		};
	}, [ref, ...dependencies]);

	return { isShow };
}
